import { useLocation, useSearchParams, Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

interface Org {
  id: string;
  name: string;
}

interface Account {
  id: string;
  name: string;
}

interface Site {
  id: string;
  account_id: string;
  name: string;
}

const HIERARCHY_PATHS = new Set(["/settings/accounts", "/settings/sites", "/settings/kitchens"]);

export function HierarchyBreadcrumb() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const headers = useApiHeaders();
  const hasAuth = !!headers.Authorization.replace("Bearer ", "");

  const accountId = searchParams.get("account");
  const siteId = searchParams.get("site");

  const isSitesPage = location.pathname === "/settings/sites";
  const isKitchensPage = location.pathname === "/settings/kitchens";
  const isHierarchyPage = HIERARCHY_PATHS.has(location.pathname);

  const orgQuery = useQuery<Org>({
    queryKey: ["organisation"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/organisations/me`, { headers });
      if (!res.ok) throw new Error("Failed to load org");
      return (await res.json()).data;
    },
    enabled: hasAuth && isHierarchyPage,
    staleTime: 5 * 60 * 1000,
  });

  const accountsQuery = useQuery<Account[]>({
    queryKey: ["accounts"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/accounts`, { headers });
      if (!res.ok) return [];
      return (await res.json()).data;
    },
    enabled: hasAuth && (isSitesPage || isKitchensPage),
    staleTime: 60 * 1000,
  });

  const sitesQuery = useQuery<Site[]>({
    queryKey: ["sites"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/sites`, { headers });
      if (!res.ok) return [];
      return (await res.json()).data;
    },
    enabled: hasAuth && isKitchensPage,
    staleTime: 60 * 1000,
  });

  if (!isHierarchyPage) return null;

  const orgName = orgQuery.data?.name ?? t("nav.organisation");
  const currentAccount = accountsQuery.data?.find((a) => a.id === accountId);
  const currentSite = sitesQuery.data?.find((s) => s.id === siteId);
  const parentAccount = currentSite
    ? accountsQuery.data?.find((a) => a.id === currentSite.account_id)
    : null;
  const accountSites = currentSite
    ? sitesQuery.data?.filter((s) => s.account_id === currentSite.account_id)
    : [];

  return (
    <nav className="flex items-center gap-1 text-sm" aria-label={t("nav.hierarchy")}>
      <Link
        to="/settings/accounts"
        className="truncate text-muted-foreground transition-colors hover:text-foreground"
      >
        {orgName}
      </Link>

      {isSitesPage && accountId && currentAccount && (
        <>
          <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
          <AccountSwitcher accounts={accountsQuery.data ?? []} currentName={currentAccount.name} />
        </>
      )}

      {isKitchensPage && siteId && currentSite && (
        <>
          <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
          <AccountSwitcher
            accounts={accountsQuery.data ?? []}
            currentName={parentAccount?.name ?? t("nav.account")}
          />
          <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
          <SiteSwitcher sites={accountSites ?? []} currentName={currentSite.name} />
        </>
      )}
    </nav>
  );
}

function AccountSwitcher({ accounts, currentName }: { accounts: Account[]; currentName: string }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-6 gap-1 px-2 text-sm font-normal">
          {currentName}
          <ChevronRight className="size-3 rotate-90 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {accounts.map((acc) => (
          <DropdownMenuItem key={acc.id} asChild>
            <Link to={`/settings/sites?account=${acc.id}`}>{acc.name}</Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SiteSwitcher({ sites, currentName }: { sites: Site[]; currentName: string }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-6 gap-1 px-2 text-sm font-normal">
          {currentName}
          <ChevronRight className="size-3 rotate-90 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {sites.map((site) => (
          <DropdownMenuItem key={site.id} asChild>
            <Link to={`/settings/kitchens?site=${site.id}`}>{site.name}</Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
